// Coordinator — WhatsApp Log (Live API data)
import { useState, useEffect } from 'react';
import { MessageSquare, ArrowUpRight, ArrowDownLeft, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';

interface WaMessage {
    id: string;
    direction: 'inbound' | 'outbound';
    templateType: string | null;
    fromNumber: string;
    toNumber: string;
    body: string;
    status: string;
    createdAt: string;
    sentAt: string | null;
    deliveredAt: string | null;
    readAt: string | null;
    patient: { firstName: string; lastName: string; whatsappNumber: string | null } | null;
}

const statusStyles: Record<string, { label: string; cls: string }> = {
    queued: { label: 'En cola', cls: 'badge-neutral' },
    sent: { label: 'Enviado', cls: 'badge-primary' },
    delivered: { label: 'Entregado', cls: 'badge-success' },
    read: { label: 'Leído', cls: 'badge-success' },
    failed: { label: 'Fallido', cls: 'badge-danger' },
};

const templateLabels: Record<string, string> = {
    booking_confirmation: 'Confirmación',
    appointment_reminder_24h: 'Recordatorio 24h',
    appointment_reminder_2h: 'Recordatorio 2h',
    consent_form: 'Consentimiento',
    prescription_ready: 'Receta',
    invoice: 'Factura',
    nps_survey: 'NPS',
    lab_results: 'Laboratorio',
    telehealth_link: 'Teleconsulta',
    general: 'General',
    follow_up: 'Seguimiento',
};

import { API_BASE } from '../config/api';

export function WhatsAppLogPage() {
    const [messages, setMessages] = useState<WaMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [dirFilter, setDirFilter] = useState<'' | 'inbound' | 'outbound'>('');

    useEffect(() => { loadMessages(); }, [dirFilter]);

    async function loadMessages() {
        setLoading(true);
        try {
            const params = dirFilter ? `?direction=${dirFilter}&limit=100` : '?limit=100';
            const res = await fetch(`${API_BASE}/whatsapp/messages${params}`);
            const json = await res.json();
            setMessages(json.data || []);
        } catch {
            console.warn('WhatsApp API unavailable');
        } finally {
            setLoading(false);
        }
    }

    const sent = messages.filter(m => m.direction === 'outbound').length;
    const received = messages.filter(m => m.direction === 'inbound').length;
    const failed = messages.filter(m => m.status === 'failed').length;

    return (
        <div className="fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <h1 className="page-title">
                    <MessageSquare size={22} style={{ display: 'inline', marginRight: 8 }} />
                    Mensajes WhatsApp
                </h1>
                <div style={{ display: 'flex', gap: 8 }}>
                    <select className="form-input" style={{ width: 140, fontSize: 13 }}
                        value={dirFilter} onChange={e => setDirFilter(e.target.value as '' | 'inbound' | 'outbound')}>
                        <option value="">Todos</option>
                        <option value="outbound">Enviados</option>
                        <option value="inbound">Recibidos</option>
                    </select>
                    <button className="btn btn-secondary" onClick={loadMessages} disabled={loading}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <RefreshCw size={14} className={loading ? 'spin' : ''} />
                    </button>
                </div>
            </div>

            <div className="stats-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#D1FAE5', color: '#16A34A' }}><ArrowUpRight size={20} /></div>
                    <div className="stat-value">{loading ? '...' : sent}</div>
                    <div className="stat-label">Enviados</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#DBEAFE', color: '#1D4ED8' }}><ArrowDownLeft size={20} /></div>
                    <div className="stat-value">{loading ? '...' : received}</div>
                    <div className="stat-label">Recibidos</div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon" style={{ background: '#FEE2E2', color: '#DC2626' }}><AlertTriangle size={20} /></div>
                    <div className="stat-value">{loading ? '...' : failed}</div>
                    <div className="stat-label">Fallidos</div>
                </div>
            </div>

            <div className="card">
                <h3>Registro de mensajes ({messages.length})</h3>
                <table className="table">
                    <thead>
                        <tr><th>Hora</th><th>Dir.</th><th>Paciente</th><th>Plantilla</th><th>Mensaje</th><th>Estado</th></tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <tr key={i}>{Array.from({ length: 6 }).map((_, j) => (
                                    <td key={j}><div style={{ height: 16, background: 'var(--bg-2)', borderRadius: 4 }} /></td>
                                ))}</tr>
                            ))
                        ) : messages.length === 0 ? (
                            <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: 'var(--text-3)' }}>No hay mensajes</td></tr>
                        ) : (
                            messages.map(m => {
                                const st = statusStyles[m.status] || { label: m.status, cls: 'badge-neutral' };
                                const time = new Date(m.createdAt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
                                const date = new Date(m.createdAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
                                const patientName = m.patient ? `${m.patient.firstName} ${m.patient.lastName}` : m.direction === 'inbound' ? m.fromNumber : m.toNumber;
                                return (
                                    <tr key={m.id} style={{ background: m.status === 'failed' ? '#FEF2F2' : undefined }}>
                                        <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                                            <div>{time}</div>
                                            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{date}</div>
                                        </td>
                                        <td>
                                            {m.direction === 'outbound'
                                                ? <ArrowUpRight size={16} color="var(--success)" />
                                                : <ArrowDownLeft size={16} color="#1D4ED8" />}
                                        </td>
                                        <td style={{ fontWeight: 500 }}>{patientName}</td>
                                        <td style={{ fontSize: 13, color: 'var(--text-2)' }}>
                                            {m.templateType ? (templateLabels[m.templateType] || m.templateType) : '—'}
                                        </td>
                                        <td style={{ fontSize: 13, color: 'var(--text-3)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {m.body}
                                        </td>
                                        <td>
                                            <span className={`badge ${st.cls}`}>
                                                {m.status === 'failed' ? <AlertTriangle size={10} style={{ marginRight: 4 }} /> :
                                                    m.status === 'read' ? <CheckCircle size={10} style={{ marginRight: 4 }} /> : null}
                                                {st.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
